import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/react-hooks";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    Heading,
    IconButton,
    LoadingProgress,
    Table,
} from "@/ui";
import routes from "config/routes/routes";
import { useHistory } from "react-router-dom";
import { replace } from "lodash";
import moment from "moment";
import { KANBAN_LANES } from "config/constant";
import { useMeta } from "@/hooks";
import { QUERY_LIST_STORE_ORDER } from "../../../apollo/module-operations/order";
import { currencyFormat } from "@/utils";
import OrderHistoryFilter from "./OrderHistoryFilter";
import styles from "./OrderHistory.module.scss";

const PAGE_SIZE = 20;

const mapOrder = node => {
    const deliveryDetails = node?.delivery_details || {};
    return {
        objectID: node.entity_id,
        entity_id: node.entity_id,
        order_number: node.order_number,
        created_at: node.created_at,
        status: node.status,
        type: deliveryDetails?.pickup
            ? "PICKUP"
            : deliveryDetails?.delivery
            ? "DELIVERY"
            : "",
        total_price: currencyFormat(node?.total_price ?? 0),
        jane_store: { id: "", name: "" },
        provider_store_name: node?.provider_store_name,
    };
};

const statusBadgeVariant = color => {
    if (color === "error") return "secondary";
    if (color === "metalic") return "neutral";
    return "primary";
};

const OrderHistory = ({ email, onListStoreOrdersData }) => {
    const history = useHistory();
    const { user } = useMeta();
    const [orders, setOrders] = useState([]);
    const [cursor, setCursor] = useState(null);
    const loadMoreRef = useRef(false);

    // Filter state: client-side filter on orders
    const [filterStatus, setFilterStatus] = useState(null);
    const [filterDateStart, setFilterDateStart] = useState(null);
    const [filterDateEnd, setFilterDateEnd] = useState(null);

    const [fetchOrderHistory, { loading, data }] = useLazyQuery(
        QUERY_LIST_STORE_ORDER,
        {
            fetchPolicy: "network-only",
            notifyOnNetworkStatusChange: true,
        },
    );

    const buildQuery = useCallback(() => {
        const escapedEmail = String(email).replace(/'/g, "\\'");
        const parts = [`email == '${escapedEmail}'`];
        if (filterDateStart != null) {
            parts.push(`created_at >= ${filterDateStart}`);
        }
        if (filterDateEnd != null) {
            parts.push(`created_at <= ${filterDateEnd}`);
        }
        if (filterStatus != null) {
            parts.push(`status == '${filterStatus}'`);
        }
        return parts.join(" && ");
    }, [email, filterDateStart, filterDateEnd, filterStatus]);

    useEffect(() => {
        if (!user?.accountId || !user?.storeId || !email) return;
        loadMoreRef.current = false;
        setCursor(null);
        const query = buildQuery();
        fetchOrderHistory({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                query,
                last: PAGE_SIZE,
            },
        });
    }, [
        user?.accountId,
        user?.storeId,
        email,
        filterDateStart,
        filterDateEnd,
        filterStatus,
        buildQuery,
        fetchOrderHistory,
    ]);

    useEffect(() => {
        if (!data?.listStoreOrders?.edges) return;

        const edges = data.listStoreOrders.edges;
        const hasPreviousPage =
            data.listStoreOrders.pageInfo?.hasPreviousPage ?? false;
        const mapped = edges.map(e => mapOrder(e.node));
        const nextCursor =
            edges.length > 0 && hasPreviousPage
                ? edges[edges.length - 1].cursor ?? null
                : null;
        setCursor(nextCursor);

        const isLoadMore = loadMoreRef.current;
        if (isLoadMore) {
            setOrders(prev => [...prev, ...mapped]);
            loadMoreRef.current = false;
        } else {
            setOrders(mapped);
            const totalCount = data.listStoreOrders.totalCount;
            const isUnfiltered =
                filterDateStart == null &&
                filterDateEnd == null &&
                filterStatus == null;
            if (
                isUnfiltered &&
                typeof totalCount === "number" &&
                onListStoreOrdersData
            ) {
                onListStoreOrdersData({ totalCount });
            }
        }
    }, [
        data,
        onListStoreOrdersData,
        filterDateStart,
        filterDateEnd,
        filterStatus,
    ]);

    const handleRefetch = useCallback(() => {
        if (!user?.accountId || !user?.storeId || !email) return;
        loadMoreRef.current = false;
        setCursor(null);
        const query = buildQuery();
        fetchOrderHistory({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                query,
                last: PAGE_SIZE,
            },
        });
    }, [user?.accountId, user?.storeId, email, buildQuery, fetchOrderHistory]);

    const handleShowMore = useCallback(() => {
        if (!user?.accountId || !user?.storeId || !email || !cursor || loading)
            return;
        loadMoreRef.current = true;
        const query = buildQuery();
        fetchOrderHistory({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                query,
                last: PAGE_SIZE,
                before: cursor,
            },
        });
    }, [
        user?.accountId,
        user?.storeId,
        email,
        cursor,
        loading,
        buildQuery,
        fetchOrderHistory,
    ]);

    // Date and status filters are applied in the server query; no client-side filtering
    const filteredOrders = orders;

    function handleDetailOpen(id) {
        const url = replace(routes.ORDER_DETAIL.route, ":id", id);
        history.push(url);
    }

    const getColorStatus = status => {
        const statusOrder = Object.entries(KANBAN_LANES).find(
            ([, laneProps]) => {
                return laneProps.status.includes(status);
            },
        );
        return statusOrder?.[1]?.color;
    };

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 56,
                minWidth: 56,
                renderCell: row => (
                    <IconButton
                        aria-label="Edit"
                        className={styles.editButton}
                        onClick={event => {
                            event.stopPropagation();
                            handleDetailOpen(row.objectID);
                        }}
                        size="sm"
                        type="button"
                        variant="tertiary"
                    >
                        <Icon icon="pencil" />
                    </IconButton>
                ),
            },
            {
                id: "order_number",
                header: "Order Number",
                width: "10%",
                minWidth: 100,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        #{row?.order_number}
                    </div>
                ),
            },
            {
                id: "created_at",
                header: "Created At",
                width: "18%",
                minWidth: 140,
                renderCell: row => {
                    const date = row.created_at
                        ? moment(row.created_at).format("MMMM Do YYYY")
                        : "";
                    const time = row.created_at
                        ? moment(row.created_at).format("HH:mm:ss")
                        : "";
                    return (
                        <div className={styles.dateCell}>
                            <div className={styles.primaryCell}>{date}</div>
                            <div className={styles.secondaryCell}>{time}</div>
                        </div>
                    );
                },
            },
            {
                id: "type",
                header: "Type",
                width: "12%",
                minWidth: 100,
                renderCell: row => (
                    <Badge
                        variant={statusBadgeVariant(
                            getColorStatus(row.status) || "info",
                        )}
                    >
                        {row.type}
                    </Badge>
                ),
            },
            {
                id: "status",
                header: "Status",
                width: "14%",
                minWidth: 120,
                renderCell: row => (
                    <Badge
                        variant={statusBadgeVariant(
                            getColorStatus(row.status) || "info",
                        )}
                    >
                        {row.status}
                    </Badge>
                ),
            },
            {
                id: "total_price",
                header: "Price",
                width: "12%",
                minWidth: 90,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        {row?.total_price || "$0"}
                    </div>
                ),
            },
            {
                id: "store",
                header: "Store",
                width: "24%",
                minWidth: 140,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        {row?.provider_store_name}
                    </div>
                ),
            },
        ],
        [],
    );

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <Heading level={5}>Order History</Heading>
                <Button
                    iconLeft={!loading ? <Icon icon="refresh" /> : undefined}
                    loading={loading}
                    onClick={handleRefetch}
                    size="sm"
                    type="button"
                    variant="secondary"
                >
                    Refresh
                </Button>
            </div>

            <div className={styles.content}>
                <aside className={styles.filterCol}>
                    <OrderHistoryFilter
                        filterDateEnd={filterDateEnd}
                        filterDateStart={filterDateStart}
                        filterStatus={filterStatus}
                        setFilterDateEnd={setFilterDateEnd}
                        setFilterDateStart={setFilterDateStart}
                        setFilterStatus={setFilterStatus}
                    />
                </aside>

                <div
                    className={`order_history__table ${styles.tableCol}`}
                    data-empty={
                        !loading && filteredOrders.length === 0
                            ? "true"
                            : undefined
                    }
                >
                    {loading && !filteredOrders.length ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading"
                            />
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            emptyMessage="No orders found"
                            getRowId={row => String(row.objectID)}
                            onRowClick={row => handleDetailOpen(row.objectID)}
                            rows={filteredOrders}
                        />
                    )}
                    {cursor ? (
                        <div className={styles.loadMore}>
                            <Button
                                disabled={loading}
                                iconLeft={
                                    !loading ? (
                                        <Icon icon="refresh" />
                                    ) : undefined
                                }
                                loading={loading}
                                onClick={handleShowMore}
                                type="button"
                                variant="secondary"
                            >
                                Show more
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default OrderHistory;
